import {
  Component,
  ChangeDetectionStrategy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  EventEmitter,
  Output,
} from '@angular/core';
import Quill from 'quill';
import { HostListener } from '@angular/core';

@Component({
  selector: 'mode-quill-editor',
  templateUrl: './quill-editor.component.html',
  styleUrls: ['./quill-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuillEditorComponent implements AfterViewInit {
  @Input() description = '';
  @Input() showTextLimitWarning = false;
  @Output() checkForTextLimit = new EventEmitter<number>();
  @Output() sendFieldDescription = new EventEmitter<string>();
  @Output() showWarning = new EventEmitter<boolean>();
  @ViewChild('editor') editor!: ElementRef;

  public quillElement!: Quill;
  public clickTracker = 0;

  @HostListener('focusout', ['$event'])
  losingFocus(event: any) {
    const focusingOutside = this.eRef.nativeElement.contains(event.relatedTarget) === false;

    if (focusingOutside) {
      this.closeEditor();
    }
  }

  @HostListener('keyup', ['$event'])
  enterKey(event: any) {
    if (event.key === 'Enter' && !event.shiftKey) {
      this.closeEditor();
    }
  }

  constructor(private eRef: ElementRef) {}

  ngAfterViewInit(): void {
    this.createQuillElement();
    this.setQuillContent();
    this.setQuillLinkPlaceHolderText();
  }

  createQuillElement(): void {
    this.quillElement = new Quill(this.editor.nativeElement, {
      modules: {
        toolbar: [['bold', 'italic', 'strike', 'link']],
        clipboard: {
          matchVisual: false,
        },
        keyboard: {
          bindings: {
            list: {
              key: 'backspace',
              context: { format: ['list'] },
              handler: (range: any) => {
                if (range.length === 0 && range.index !== 0) {
                  this.quillElement.deleteText(range.index - 1, 1, Quill.sources.USER);
                } else {
                  this.quillElement.deleteText(range, Quill.sources.USER);
                }
              },
            },
            enter: {
              key: 13,
              handler: () => {
                /* Disables new line on enter */
              },
            },
          },
        },
      },
      theme: 'snow',
    });
    this.removeHTMLImages();
    this.quillElement.focus();
  }

  removeHTMLImages(): void {
    this.quillElement.root.addEventListener('paste', (e: any) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text/html');
      const sanitizedData = this.detectAndRemoveImages(pastedData);
      //this index needs the -1 so that it doesn't always paste content to a new line
      const index = this.quillElement.getLength() - 1;
      this.quillElement.clipboard.dangerouslyPasteHTML(index, sanitizedData);
      this.setCursorPosition();
    });
  }

  setCursorPosition() {
    const index = this.quillElement.getLength();
    /*set timeout is needed to ensure dangerouslyPasteHTML
    is complete before setting the cursor position*/
    setTimeout(() => {
      this.quillElement.setSelection(index);
    }, 1);
  }

  detectAndRemoveImages(pastedData: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = pastedData;
    const images = tempDiv.getElementsByTagName('img');

    for (let i = 0; i < images.length; i++) {
      images[i]?.parentNode?.removeChild(images[i]);
    }

    return tempDiv.innerHTML;
  }

  setQuillContent(): void {
    this.quillElement.root.innerHTML = this.description;
  }

  setQuillLinkPlaceHolderText(): void {
    /** In Quill's link text editor input, the placeholder is default https://quilljs.com.  We are finding and overriding
    to be https://app.mode.com.
    github issue fix ref https://github.com/slab/quill/issues/1107
    */
    const quillLinkInput = (this.quillElement.theme as any).tooltip.root.querySelector('input[data-link]');
    if (quillLinkInput) {
      quillLinkInput.dataset.link = 'https://app.mode.com';
    }
  }

  closeEditor() {
    const textLength = this.quillElement.getText().trim().length;
    if (textLength > 0) {
      this.sendFieldDescription.emit(this.quillElement.getSemanticHTML());
    } else {
      this.sendFieldDescription.emit('');
    }
  }

  checkTextSize(): void {
    this.quillElement.on('text-change', () => {
      this.checkForTextLimit.emit(this.quillElement.getText().length);
    });
  }
}
